import { useCallback, useEffect, useState } from "react";

function storageKeyFor(userId: string, auditId: string): string {
  return `collapsed-sections:${userId}:${auditId}`;
}

function loadCollapsed(userId: string, auditId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId, auditId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === "string"));
  } catch {
    return new Set();
  }
}

function saveCollapsed(userId: string, auditId: string, collapsed: Set<string>): void {
  try {
    localStorage.setItem(storageKeyFor(userId, auditId), JSON.stringify([...collapsed]));
  } catch {}
}

/**
 * Tracks which sections of an audit's checklist the current user has
 * collapsed. Persisted per (user, audit) in localStorage — private to this
 * user's browser, so it never affects what another user sees when viewing
 * the same audit.
 */
export function useCollapsedSections(auditId: string, userId: string | undefined) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    userId ? loadCollapsed(userId, auditId) : new Set(),
  );

  // Re-read when the audit or the resolved session user changes (e.g. the
  // session hook resolves after the initial render).
  useEffect(() => {
    setCollapsed(userId ? loadCollapsed(userId, auditId) : new Set());
  }, [auditId, userId]);

  const toggleSection = useCallback(
    (sectionTitle: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(sectionTitle)) {
          next.delete(sectionTitle);
        } else {
          next.add(sectionTitle);
        }
        if (userId) saveCollapsed(userId, auditId, next);
        return next;
      });
    },
    [auditId, userId],
  );

  const isSectionCollapsed = useCallback(
    (sectionTitle: string) => collapsed.has(sectionTitle),
    [collapsed],
  );

  return { isSectionCollapsed, toggleSection };
}
